import { useEffect, useState } from "react";
import { api, type AssetStub } from "../lib/api";

type Tab = "mine" | "favorites" | "samples";

interface AssetPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (webKey: string, thumbUrl: string) => void;
}

export function AssetPicker({ open, onClose, onSelect }: AssetPickerProps) {
  const [assets, setAssets] = useState<AssetStub[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<Tab>("mine");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSearch("");
    api.listAllAssets()
      .then((list) => setAssets(list ?? []))
      .catch(() => setAssets([]))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  async function toggleFavorite(asset: AssetStub) {
    const next = !asset.is_favorite;
    // Optimistic update
    setAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, is_favorite: next } : a)));
    try {
      await api.toggleAssetFavorite(asset.id, next);
    } catch {
      // Roll back
      setAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, is_favorite: asset.is_favorite } : a)));
    }
  }

  const lowerSearch = search.toLowerCase();
  const tabFiltered =
    tab === "favorites" ? assets.filter((a) => a.is_favorite) :
    tab === "mine" ? assets :
    []; // samples tab handled separately

  const filtered = search
    ? tabFiltered.filter(
        (a) =>
          a.filename.toLowerCase().includes(lowerSearch) ||
          a.gallery_title.toLowerCase().includes(lowerSearch),
      )
    : tabFiltered;

  // Group by gallery for the Mine tab; flat for Favorites.
  const grouped = tab === "mine"
    ? filtered.reduce<Record<string, { title: string; assets: AssetStub[] }>>(
        (acc, a) => {
          if (!acc[a.gallery_id]) acc[a.gallery_id] = { title: a.gallery_title, assets: [] };
          acc[a.gallery_id].assets.push(a);
          return acc;
        }, {})
    : null;

  const favoriteCount = assets.filter((a) => a.is_favorite).length;

  return (
    <>
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 300 }}
        onClick={onClose}
      />
      <div
        style={{
          position: "fixed", top: "5vh", left: "50%",
          transform: "translateX(-50%)",
          width: "min(860px,95vw)", maxHeight: "85vh",
          background: "var(--card-bg, var(--card, #1a2332))",
          borderRadius: 12, zIndex: 301,
          display: "flex", flexDirection: "column", overflow: "hidden",
          boxShadow: "0 8px 40px rgba(0,0,0,.5)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "0.85rem 1.25rem", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0, fontSize: "1rem" }}>Choose an image</h3>
          {tab !== "samples" && (
            <input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{
                flex: 1, padding: ".4rem .75rem", borderRadius: 6,
                border: "1px solid var(--border)",
                background: "var(--card-bg, var(--card))",
                color: "var(--text)",
              }}
            />
          )}
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.25rem", color: "var(--muted)", lineHeight: 1, padding: "0 .25rem" }}
            aria-label="Close"
          >✕</button>
        </div>
        {/* Tabs */}
        <div style={{ display: "flex", gap: "0.25rem", padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--border)" }}>
          <TabButton active={tab === "mine"} onClick={() => setTab("mine")}>
            My photos <span style={{ opacity: .6 }}>· {assets.length}</span>
          </TabButton>
          <TabButton active={tab === "favorites"} onClick={() => setTab("favorites")}>
            ★ Favorites <span style={{ opacity: .6 }}>· {favoriteCount}</span>
          </TabButton>
          <TabButton active={tab === "samples"} onClick={() => setTab("samples")}>
            Sample images
          </TabButton>
        </div>
        {/* Body */}
        <div style={{ overflowY: "auto", padding: "1rem 1.25rem", flex: 1 }}>
          {tab === "samples" ? (
            <SamplesPlaceholder />
          ) : loading ? (
            <p style={{ color: "var(--muted)" }}>Loading…</p>
          ) : assets.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>
              <p>No images yet. Upload photos to your galleries first, or try the Sample images tab to fill in a page.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}>
              <p>
                {tab === "favorites"
                  ? "No favorites yet — open My photos and tap the ★ on any image to add it here."
                  : "No images match your search."}
              </p>
            </div>
          ) : grouped ? (
            Object.entries(grouped).map(([gid, group]) => (
              <div key={gid} style={{ marginBottom: "1.5rem" }}>
                <div style={{ fontSize: ".75rem", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--muted)", marginBottom: ".5rem" }}>
                  {group.title}
                </div>
                <AssetGrid assets={group.assets} onSelect={onSelect} onClose={onClose} onToggleFavorite={toggleFavorite} />
              </div>
            ))
          ) : (
            <AssetGrid assets={filtered} onSelect={onSelect} onClose={onClose} onToggleFavorite={toggleFavorite} />
          )}
        </div>
      </div>
    </>
  );
}

function TabButton({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: ".35rem .75rem", borderRadius: 6,
        border: `1px solid ${active ? "var(--accent, #10b981)" : "var(--border)"}`,
        background: active ? "color-mix(in srgb, var(--accent, #10b981) 12%, transparent)" : "transparent",
        color: active ? "var(--accent, #10b981)" : "var(--text)",
        fontSize: ".85rem", fontWeight: active ? 600 : 500, cursor: "pointer",
      }}
    >{children}</button>
  );
}

function AssetGrid({
  assets, onSelect, onClose, onToggleFavorite,
}: {
  assets: AssetStub[];
  onSelect: (webKey: string, thumbUrl: string) => void;
  onClose: () => void;
  onToggleFavorite: (a: AssetStub) => void;
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(100px,1fr))", gap: 6 }}>
      {assets.map((a) => (
        <div
          key={a.id}
          style={{
            position: "relative", aspectRatio: "1", overflow: "hidden",
            borderRadius: 6, cursor: "pointer",
            border: "2px solid transparent", transition: "border-color .15s",
            background: "var(--border)",
          }}
          onClick={() => { onSelect(a.web_key, a.thumb_url); onClose(); }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--accent, #10b981)")}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "transparent")}
          title={a.filename}
        >
          <img
            src={a.thumb_url || undefined}
            alt={a.filename}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            loading="lazy"
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleFavorite(a); }}
            title={a.is_favorite ? "Remove from favorites" : "Add to favorites"}
            style={{
              position: "absolute", top: 4, right: 4,
              width: 26, height: 26, borderRadius: "50%",
              background: a.is_favorite ? "#facc15" : "rgba(0,0,0,.55)",
              color: a.is_favorite ? "#0f172a" : "#fff",
              border: "none", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: ".85rem", lineHeight: 1, boxShadow: "0 1px 3px rgba(0,0,0,.4)",
            }}
            aria-label={a.is_favorite ? "Remove from favorites" : "Mark as favorite"}
          >{a.is_favorite ? "★" : "☆"}</button>
        </div>
      ))}
    </div>
  );
}

function SamplesPlaceholder() {
  return (
    <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: "var(--muted)" }}>
      <div style={{ fontSize: "2rem", marginBottom: ".5rem" }}>🖼️</div>
      <h4 style={{ margin: "0 0 0.5rem", color: "var(--text)" }}>Sample images — coming soon</h4>
      <p style={{ maxWidth: 460, margin: "0 auto", fontSize: ".9rem" }}>
        A curated library of stock photos you can drop into a page in one click — handy
        when you're laying out a new page before your own shoot delivers. We're sourcing
        the first batch now; when it lands you'll see it here.
      </p>
      <p style={{ maxWidth: 460, margin: "1rem auto 0", fontSize: ".85rem", color: "var(--muted)" }}>
        In the meantime: ★ a few of your own photos in <strong>My photos</strong> and switch to
        the <strong>Favorites</strong> tab — your go-to images stay one click away across every
        page you edit.
      </p>
    </div>
  );
}
